/**
 * Заявка из нескольких разовых мест (BLUEPRINT Stage 17, ADR-35). Чистые функции: пересечение по
 * времени, итог заявки, сколько разовых мест на сборе, цена и зачёт разового места, лимит без
 * сезона. Деньги — в копейках, округление `Math.round`, как в `domain/pricing` и `domain/seats`.
 */

import { BOOKING_CLOSE, BOOKING_DEFAULTS, isReleasedItem } from '../constants/booking';
import { SETTING_MULTIPLIER_MAX } from '../constants/pricing';
import { SQUAD_DEFAULTS } from '../constants/squad';
import {
  GatheringStatus,
  OrderKind,
  OrderStatus,
  SeasonStatus,
  SquadStatus,
  type GatheringStatus as GatheringStatusT,
  type OrderKind as OrderKindT,
  type OrderStatus as OrderStatusT,
  type SeasonStatus as SeasonStatusT,
  type SquadStatus as SquadStatusT,
} from '../enums';
import type { BookingCloseKind } from '../schemas/order';
import { seatShowcaseOpen } from './seats';

const MINUTE_MS = 60_000;
const DAY_MS = 86_400_000;

// ───────────────────────────── время ─────────────────────────────

export interface TimeSlot {
  startsAt: Date;
  durationMin: number;
}

/**
 * Пересекаются ли два сбора по времени. Интервалы полуоткрытые `[начало, конец)`: сбор,
 * который начинается ровно в момент окончания другого, не пересекается с ним.
 */
export function overlaps(a: TimeSlot, b: TimeSlot): boolean {
  const aStart = a.startsAt.getTime();
  const bStart = b.startsAt.getTime();
  const aEnd = aStart + Math.max(0, a.durationMin) * MINUTE_MS;
  const bEnd = bStart + Math.max(0, b.durationMin) * MINUTE_MS;
  return aStart < bEnd && bStart < aEnd;
}

// ───────────────────────────── итог заявки ─────────────────────────────

export interface BundleLine {
  /** Цена позиции (`singlePrice`, `pricing.substitute`, `pricing.trial`) */
  priceKopecks: number;
  /** Позицию можно оплатить; «слот забрали», пересечение, лимит — false, в итог не входит */
  available: boolean;
}

export interface BundleLineTotal {
  /** Доля реферального бонуса на позиции */
  discountKopecks: number;
  /** К оплате по позиции (`Order.amountKopecks` позиции); у недоступной — 0 */
  amountKopecks: number;
}

export interface BundleTotal {
  /** Сумма цен доступных позиций */
  listKopecks: number;
  /** Реферальный бонус, который реально списан (не больше суммы) */
  referralBonusKopecks: number;
  /** К оплате одной ссылкой (`Order.amountKopecks` родителя) = сумма `lines[].amountKopecks` */
  amountKopecks: number;
  lines: BundleLineTotal[];
}

function kopecks(value: number): number {
  return Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0;
}

/**
 * Итог заявки. Реферальный бонус — один на заявку (ADR-24) — делится между доступными позициями
 * пропорционально цене (метод наибольшего остатка), чтобы сумма позиций всегда равнялась сумме
 * родителя, а возврат позиции был ровно тем, что за неё заплачено.
 */
export function bundleTotal(lines: readonly BundleLine[], referralBonusKopecks = 0): BundleTotal {
  const prices = lines.map((l) => (l.available ? kopecks(l.priceKopecks) : 0));
  const list = prices.reduce((s, p) => s + p, 0);
  const bonus = Math.min(list, kopecks(referralBonusKopecks));

  const shares = prices.map((p) => (list > 0 ? Math.floor((bonus * p) / list) : 0));
  let rest = bonus - shares.reduce((s, x) => s + x, 0);
  const order = prices
    .map((p, i) => ({ i, rem: list > 0 ? (bonus * p) % list : 0 }))
    .filter((x) => x.rem > 0)
    .sort((a, b) => b.rem - a.rem || a.i - b.i);
  for (const { i } of order) {
    if (rest <= 0) break;
    shares[i] = (shares[i] ?? 0) + 1;
    rest -= 1;
  }

  const out = prices.map((p, i) => {
    const discount = shares[i] ?? 0;
    return { discountKopecks: discount, amountKopecks: p - discount };
  });
  return {
    listKopecks: list,
    referralBonusKopecks: bonus,
    amountKopecks: list - bonus,
    lines: out,
  };
}

// ───────────────────────────── места на сборе ─────────────────────────────

export interface SingleSeatsInput {
  /** `Squad.capacity` */
  squadCapacity: number;
  /** `Gathering.capacity` (ops у разового сбора); null — вместимость отряда */
  gatheringCapacity: number | null;
  /** `Venue.capacity`, если известна */
  venueCapacity: number | null;
  /** `booking.singleShareMax`, доля в (0, 1] */
  shareMax: number;
  seasonStatus: SeasonStatusT;
  squadStatus: SquadStatusT;
  gatheringStatus: GatheringStatusT;
  /** Капитан закрыл сбор для разовых (`Gathering.singlesClosed`) */
  singlesClosed: boolean;
  startsAt: Date;
  now: Date;
  /** Состав отряда на этом сборе без ответа «не иду» */
  squadGoing: number;
  /** Замены на сборе (занятые и в живой брони) — в долю разовых не входят */
  substitutes: number;
  /** Пробные на сборе (занятые и в живой брони) */
  trials: number;
  /** Разовые места на сборе (занятые и в живой брони) */
  singles: number;
  /** Живые места отряда, открытые на сезон (`OpenSeat SEASON`): их держит приоритет сезона */
  openSeasonSeats: number;
  /** `booking.singleSaleDays`: ближе этого срока место на сезон можно продать разовым */
  singleSaleDays: number;
}

/** Вместимость сбора: `Gathering.capacity`, если её задал ops, иначе `Squad.capacity`. */
export function gatheringCapacityOf(
  squadCapacity: number,
  gatheringCapacity: number | null,
): number {
  return gatheringCapacity ?? squadCapacity;
}

/** Сколько мест на сборе под разовые и пробные всего: `floor(вместимость × singleShareMax)`. */
export function singleShareSeats(capacity: number, shareMax: number): number {
  const share = Number.isFinite(shareMax) && shareMax > 0 && shareMax <= 1 ? shareMax : 0;
  // 1e-9 гасит ошибку плавающей точки (0,29 × 100 = 28,999…), не меняя честных дробей
  return Math.max(0, Math.floor(Math.max(0, capacity) * share + 1e-9));
}

/**
 * Сколько разовых мест можно продать на сбор сейчас (ADR-35 п. 2). Минимум из доли разовых
 * (минус занятые разовые и пробные), свободных мест сбора и остатка вместимости площадки.
 * Ноль, если сезон или отряд не `ACTIVE`, капитан закрыл сбор, сбор отменён или прошёл, или
 * до начала меньше `SEATS.closeBeforeHours`. Место, открытое на сезон, отдаётся разовым только
 * на сбор ближе `singleSaleDays` дней (приоритет сезона).
 */
export function singleSeatsFor(input: SingleSeatsInput): number {
  if (input.seasonStatus !== SeasonStatus.ACTIVE) return 0;
  if (input.squadStatus !== SquadStatus.ACTIVE) return 0;
  if (
    input.gatheringStatus !== GatheringStatus.PLANNED &&
    input.gatheringStatus !== GatheringStatus.CONFIRMED
  ) {
    return 0;
  }
  if (input.singlesClosed) return 0;
  if (!seatShowcaseOpen(input.now, input.startsAt)) return 0;

  const capacity = gatheringCapacityOf(input.squadCapacity, input.gatheringCapacity);
  const guests = Math.max(0, input.trials) + Math.max(0, input.singles);
  const occupied = Math.max(0, input.squadGoing) + Math.max(0, input.substitutes) + guests;

  const byShare = singleShareSeats(capacity, input.shareMax) - guests;
  let free = capacity - occupied;
  const saleWindowMs = Math.max(0, input.singleSaleDays) * DAY_MS;
  if (input.startsAt.getTime() - input.now.getTime() > saleWindowMs) {
    free -= Math.max(0, input.openSeasonSeats);
  }
  const byVenue = input.venueCapacity === null ? Infinity : input.venueCapacity - occupied;

  return Math.max(0, Math.min(byShare, free, byVenue));
}

export interface PlaceOrderState {
  status: OrderStatusT;
  parentOrderId: string | null;
  refundReason: string | null;
}

/**
 * Держит ли заказ разового места, замены или пробного место на сборе: оплаченный и не снятый
 * «к возврату» (`isReleasedItem`) — да; `PENDING` — только позиция живой заявки (одиночный
 * неоплаченный счёт Stage 11 места не держит), кроме заявки `ignoreParentId`, которую сейчас
 * заменяют. Одно правило для заявок, места на сезон и хука инвариантов.
 */
export function orderHoldsPlace(o: PlaceOrderState, ignoreParentId: string | null = null): boolean {
  if (o.status === OrderStatus.PENDING) {
    return o.parentOrderId !== null && o.parentOrderId !== ignoreParentId;
  }
  if (o.status === OrderStatus.PAID || o.status === OrderStatus.PARTIALLY_REFUNDED) {
    return !isReleasedItem(o.refundReason);
  }
  return false;
}

/**
 * Почему заявка закрыта без оплаты: бронь истекла, человек собрал другую заявку, снял её сам
 * (раунд персон 3, N10) или её закрыл ops. Одно правило для экрана заявки (`bookings.status`) и
 * строки «Оплат и счетов» (`payments.my`, N11): `orders` не может импортировать `bookings`.
 * Не закрытая заявка и не заявка — null.
 */
export function bookingCloseKind(o: {
  kind: OrderKindT;
  status: OrderStatusT | null;
  refundReason: string | null;
}): BookingCloseKind | null {
  if (o.kind !== OrderKind.BUNDLE || o.status !== OrderStatus.CANCELLED) return null;
  if (o.refundReason === BOOKING_CLOSE.holdExpired) return 'hold_expired';
  if (o.refundReason === BOOKING_CLOSE.replaced) return 'replaced';
  if (o.refundReason === BOOKING_CLOSE.userCancelled) return 'user_cancelled';
  // `ops_cancelled` или своя причина ops в «Заказах» (`cancelBookingByOps`)
  return 'ops_cancelled';
}

// ───────────────────────────── цена и зачёт ─────────────────────────────

/**
 * Цена разового места: `round(цена сезона / сборов в сезоне × pricing.singleRatio)` (ADR-35 п. 3).
 * Падел по умолчанию: 7 500 ₽ / 6 × 1,3 = 1 625 ₽. Скидки капитана и продления не применяются.
 * Битый множитель — дефолт `BOOKING_DEFAULTS.singleRatio`, битое число сборов — 6.
 */
export function singlePrice(
  seasonPriceKopecks: number,
  singleRatio: number,
  gatheringsPerSeason: number = SQUAD_DEFAULTS.gatheringsPerSeason,
): number {
  const base = kopecks(seasonPriceKopecks);
  const ratio =
    Number.isFinite(singleRatio) && singleRatio > 0 && singleRatio <= SETTING_MULTIPLIER_MAX
      ? singleRatio
      : BOOKING_DEFAULTS.singleRatio;
  const n =
    Number.isInteger(gatheringsPerSeason) && gatheringsPerSeason > 0
      ? gatheringsPerSeason
      : SQUAD_DEFAULTS.gatheringsPerSeason;
  return Math.round((base * ratio) / n);
}

export interface SingleCreditCandidate {
  orderId: string;
  kind: OrderKindT;
  status: OrderStatusT;
  paidAt: Date | null;
  amountKopecks: number;
  refundedKopecks: number;
  /** Город сбора позиции */
  cityId: string | null;
  /** Уже вошла в живой (не отменённый) заказ сезона (`Order.creditedToOrderId`) */
  alreadyCredited: boolean;
}

export interface SingleCreditInput {
  candidates: readonly SingleCreditCandidate[];
  /** Город сезона, который покупают */
  seasonCityId: string;
  /** Сколько ещё можно зачесть: цена сезона после остальных скидок */
  capKopecks: number;
  /** `pricing.singleCreditDays` */
  creditDays: number;
  now: Date;
}

export interface SingleCreditResult {
  creditKopecks: number;
  /** Позиции, вошедшие в зачёт (им ставится `creditedToOrderId`) */
  orderIds: string[];
}

const CREDITABLE_KINDS: readonly OrderKindT[] = [OrderKind.SINGLE, OrderKind.SUBSTITUTE];
const PAID_STATUSES: readonly OrderStatusT[] = [OrderStatus.PAID, OrderStatus.PARTIALLY_REFUNDED];

/**
 * Зачёт разовых мест в сезон (ADR-35 п. 3): оплаченные и не возвращённые `SINGLE`/`SUBSTITUTE`
 * этого человека в том же городе, оплаченные не раньше `creditDays` дней назад и ещё не зачтённые.
 * Не больше `capKopecks`; каждая позиция зачитывается один раз, старые — первыми. Пробный сбор
 * идёт своим правилом (`creditFromOrderId`) и сюда не попадает.
 */
export function singleCredit(input: SingleCreditInput): SingleCreditResult {
  const nowMs = input.now.getTime();
  const windowMs = Math.max(0, input.creditDays) * DAY_MS;
  const eligible = input.candidates
    .filter(
      (c) =>
        CREDITABLE_KINDS.includes(c.kind) &&
        PAID_STATUSES.includes(c.status) &&
        !c.alreadyCredited &&
        c.cityId === input.seasonCityId &&
        c.paidAt !== null &&
        c.paidAt.getTime() <= nowMs &&
        nowMs - c.paidAt.getTime() <= windowMs &&
        kopecks(c.amountKopecks) - kopecks(c.refundedKopecks) > 0,
    )
    .sort(
      (a, b) =>
        (a.paidAt?.getTime() ?? 0) - (b.paidAt?.getTime() ?? 0) ||
        a.orderId.localeCompare(b.orderId),
    );

  let left = kopecks(input.capKopecks);
  let credit = 0;
  const orderIds: string[] = [];
  for (const c of eligible) {
    if (left <= 0) break;
    const net = kopecks(c.amountKopecks) - kopecks(c.refundedKopecks);
    const take = Math.min(net, left);
    credit += take;
    left -= take;
    orderIds.push(c.orderId);
  }
  return { creditKopecks: credit, orderIds };
}

// ───────────────────────────── лимит без сезона ─────────────────────────────

export interface NoSeasonLimitInput {
  /** Оплаченный сезон в `SALE`/`ACTIVE` — лимита нет */
  hasSeason: boolean;
  /** Начала сборов уже взятых разовых позиций (`SINGLE`, `SUBSTITUTE`, `TRIAL`: оплачены или в живой брони, не возвращены) */
  existing: readonly Date[];
  /** Начала сборов новых позиций заявки */
  adding: readonly Date[];
  /** `booking.noSeasonMax` */
  max: number;
  /** `booking.noSeasonWindowDays` */
  windowDays: number;
}

/**
 * Упрётся ли заявка в лимит без сезона (ADR-35 п. 4): в любом окне длиной `windowDays` дней
 * по датам сборов, где есть хоть одна новая позиция, позиций больше `max`. Окно скользящее,
 * а не «от сегодня»: три разовых на этой неделе и одна через месяц — не нарушение, если между
 * ними больше окна.
 */
export function noSeasonLimitReached(input: NoSeasonLimitInput): boolean {
  if (input.hasSeason || input.adding.length === 0) return false;
  const max = Math.max(0, Math.floor(input.max));
  const windowMs = Math.max(0, input.windowDays) * DAY_MS;
  const points = [
    ...input.existing.map((d) => ({ t: d.getTime(), added: false })),
    ...input.adding.map((d) => ({ t: d.getTime(), added: true })),
  ].sort((a, b) => a.t - b.t);

  // Окно с наибольшим числом позиций всегда можно сдвинуть так, чтобы оно начиналось с позиции
  for (const { t: start } of points) {
    const inWindow = points.filter((p) => p.t >= start && p.t < start + windowMs);
    if (inWindow.length > max && inWindow.some((p) => p.added)) return true;
  }
  return false;
}

export interface NoSeasonUsageInput {
  /** Начала сборов уже взятых разовых позиций (как в `noSeasonLimitReached`) */
  existing: readonly Date[];
  /** Начала сборов, выбранных сейчас (витрина, ещё не заявка) */
  adding: readonly Date[];
  windowDays: number;
  now: Date;
}

export interface NoSeasonUsage {
  /** Сколько позиций в текущем окне */
  used: number;
  /** Когда текущее окно закончится (начало первой позиции окна + `windowDays`); null — позиций нет */
  until: Date | null;
}

/**
 * Счётчик «Разовых без сезона: N из 3 до <даты>» (раунд персон 1, N5). Текущее окно — от первой
 * позиции, которая ещё в окне от сегодня (сбор не раньше `now − windowDays`), длиной `windowDays`;
 * N — позиции в нём. Это подпись, а не проверка: решает всегда `noSeasonLimitReached` со
 * скользящим окном (строже на сборах далеко впереди).
 */
export function noSeasonUsage(input: NoSeasonUsageInput): NoSeasonUsage {
  const windowMs = Math.max(0, input.windowDays) * DAY_MS;
  const from = input.now.getTime() - windowMs;
  const points = [...input.existing, ...input.adding]
    .map((d) => d.getTime())
    .filter((t) => t >= from)
    .sort((a, b) => a - b);
  const first = points[0];
  if (first === undefined) return { used: 0, until: null };
  return {
    used: points.filter((t) => t < first + windowMs).length,
    until: new Date(first + windowMs),
  };
}
